"use client";
import "./PetStyles.css";
import "./MyPetsDash.css";

import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DeleteConfirmationModal from "../../components/modals/DeleteConfirmation";
import Navbar from "../../components/account/Navbar";
import { usePets } from "../../hooks/usePets";
import { useAuth } from "../../hooks/useAuth";

import Footer from "../../components/account/Footer";
import GuardianshipRequests from "../../components/petProfiles/GuardianshipRequests";
export default function MyPetsDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { userType } = useParams();
  const { getUserType, user } = useAuth();

  // Get user type from auth context (more reliable than URL params)
  const currentUserType = getUserType();

  // RTL support
  const isRTL = i18n.language === "ar";

  // Check if user came from profile page
  const [cameFromProfile, setCameFromProfile] = useState(false);

  useEffect(() => {
    // Check location state first (if navigation passed state)
    if (location.state?.fromProfile) {
      setCameFromProfile(true);
      return;
    }

    // Fallback: check document referrer
    const referrer = document.referrer;
    if (referrer && referrer.includes('/profile')) {
      setCameFromProfile(true);
    }
  }, [location.state]);

  // Authentication is now handled by useAuth hook globally
  // No need for individual page authentication checks

  const {
    pets,
    loading,
    error,
    deletePet,
    refresh,
    isEmpty,
    hasError,
  } = usePets();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // Scroll lock for modal
  useEffect(() => {
    if (showDeleteModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showDeleteModal]);

  const [petToDelete, setPetToDelete] = useState(null);
  const [deletingPet, setDeletingPet] = useState(false);


  const getColorScheme = (userType) => {
    switch (userType) {
      case "PET_GUARDIAN":
        return { primary: "#8548A8", hover: "#7A4397" };
      case "VETERINARIAN":
        return { primary: "#00D4B3", hover: "#00C4A7" };
      case "SERVICE_PROVIDER":
        return { primary: "#D65745", hover: "#C7493A" };
      case "FIRM":
        return { primary: "#1B3A6B", hover: "#152E55" };
      default:
        return { primary: "#8548A8", hover: "#7A4397" };
    }
  };

  const colors = getColorScheme(currentUserType);

  // User type color configuration for dynamic styling
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8', // Purple
      'VETERINARIAN': '#00D4B3', // Cyan
      'SERVICE_PROVIDER': '#D65745', // Orange
      'CLINIC_MODERATOR': '#2A8AD2', // Blue
      'FIRM': '#1B3A6B', // Navy Blue
    };
    return userTypeConfig[userType] || '#8548A8'; // Default to purple
  };

  // Get the current user type color
  const userTypeColor = getUserTypeColor(currentUserType);

  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Accent vars available to everything under .mypets-root
  const rootStyle = {
    '--user-type-color': userTypeColor,
    '--user-type-color-soft': hexToRgba(userTypeColor, 0.08),
    '--user-type-color-softer': hexToRgba(userTypeColor, 0.04),
    '--user-type-color-line': hexToRgba(userTypeColor, 0.28),
    '--user-type-color-shadow': hexToRgba(userTypeColor, 0.22),
  };

  // Prefer server-provided pet images with robust fallbacks
  const resolvePetImageSrc = (pet) => {
    if (!pet) return "/images/no-image-pet.png";

    // Handle profile_image_path which might be a string or an object { url, path, ... }
    const profilePath =
      typeof pet.profile_image_path === "string"
        ? pet.profile_image_path
        : pet.profile_image_path?.url || pet.profile_image_path?.path;

    const candidates = [
      // Transformed field from usePets (primaryImageUrl)
      pet.primaryImageUrl,
      // Raw backend-style field if ever passed through
      pet.primary_image_url,
      profilePath,
      pet.image,
      pet.avatar,
      pet.profile_image_url,
      pet.profileImageUrl,
      pet.photo,
      Array.isArray(pet.pictures) ? pet.pictures?.[0]?.url : undefined,
      Array.isArray(pet.media) ? pet.media?.[0]?.url : undefined,
    ];

    const chosen = candidates.find(
      (u) => typeof u === "string" && u.trim() !== ""
    );
    return chosen || "/images/no-image-pet.png";
  };

  const handleAddNewPet = () => {
    navigate(`/pets/add`);
  };

  const handleSubmitEmergency = () => {
    navigate(`/medical/dashboard/petguardian/urgent-case-submit`);
  };

  const handlePetAction = (action, petId) => {
    switch (action) {
      case "view":
        navigate(`/pets/${petId}/view`);
        break;
      case "update":
        navigate(`/pets/${petId}/edit`);
        break;
      case "delete":
        setPetToDelete(petId);
        setShowDeleteModal(true);
        break;
    }
  };

  const handleConfirmDelete = async () => {
    if (petToDelete) {
      setDeletingPet(true);
      const result = await deletePet(petToDelete);
      setDeletingPet(false);

      if (result.success) {
        setShowDeleteModal(false);
        setPetToDelete(null);
      }
    }
  };
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setPetToDelete(null);
  };

  const handleBackToProfile = () => {
    navigate('/social/profile');
  };

  // Loading state
  if (loading) {
    return (
      <div className="mypets-root" style={rootStyle}>
        <Navbar userType={userType || "PET_GUARDIAN"} />
        <main className="mypets-main">
          <section className="page-frame mypets-section">
            <div className="mypets-controls">
              <div className={`mypets-controls-header ${isRTL ? 'rtl' : 'ltr'}`}>
                <div className="mp-skel mp-skel-title" />
                <div className="mypets-buttons">
                  <div className="mp-skel mp-skel-btn" />
                  <div className="mp-skel mp-skel-btn" />
                </div>
              </div>
            </div>
            <div className="pets-grid mypets-grid" aria-hidden="true">
              {[1, 2, 3, 4].map(i => (
                <div key={`mp-skel-${i}`} className="mypets-card mp-skel-card">
                  <div className="mp-skel mp-skel-pet-image" />
                  <div
                    className="mypets-card-content"
                    style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                  >
                    <div className="mp-skel mp-skel-line mp-skel-w40" />
                    <div className="mp-skel mp-skel-line mp-skel-w60" />
                    <div className="mp-skel mp-skel-pills" />
                    <div className="mp-skel mp-skel-actions" />
                  </div>
                </div>
              ))}
            </div>
          </section>
        </main>
      </div>
    );
  }

  // Error state with retry option
  if (hasError && isEmpty) {
    return (
      <div className="mypets-root" style={rootStyle}>
        <Navbar userType={userType || "PET_GUARDIAN"} />
        <main className="mypets-main mypets-center-content mypets-error-content">
          <div className="mypets-error-text">
            {error?.includes("401")
              ? t("petDashboard.authenticationFailed")
              : `${t("petDashboard.error")}: ${error}`}
          </div>
          <div className="mypets-error-buttons">
            <button
              onClick={refresh}
              className="mypets-btn-primary"
            >
              {t("petDashboard.tryAgain")}
            </button>
            {error?.includes("401") && (
              <button
                onClick={() => navigate("/auth/login")}
                className="mypets-btn-error"
              >
                {t("petDashboard.login")}
              </button>
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="mypets-root" style={rootStyle}>
      <Navbar userType={userType || "PET_GUARDIAN"} />

      <main className="mypets-main">
        <GuardianshipRequests onRequestsUpdated={() => refresh()} />
        <section className="page-frame mypets-section">
          <div className="mypets-controls">
            <div className={`mypets-controls-header ${isRTL ? 'rtl' : 'ltr'}`}>
              <div className="mypets-header-title-wrapper">
                {cameFromProfile && (
                  <button
                    onClick={handleBackToProfile}
                    className="mypets-btn-back"
                    style={{
                      '--user-type-color': userTypeColor
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      style={{ transform: isRTL ? 'scaleX(-1)' : 'none' }}
                    >
                      <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    <span className="mypets-btn-back-text">{t("petDashboard.backToProfile")}</span>
                  </button>
                )}
                <h2 className="mypets-section-title" style={{ '--user-type-color': userTypeColor }}>
                  {t("petDashboard.myPets")} {pets.length > 0 && `(${pets.length})`}
                </h2>
              </div>

              <div className="mypets-buttons">
                <button
                  onClick={handleAddNewPet}
                  className="mypets-btn-add"
                  data-tour="add-pet"
                  style={{
                    '--user-type-color': userTypeColor
                  }}
                >
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="mypets-btn-icon"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
                  </svg>
                  <span className="mypets-btn-text">{t("petDashboard.addNewPet")}</span>
                </button>

                {/* Hide emergency button for veterinarians and firms */}
                {currentUserType !== "VETERINARIAN" && currentUserType !== "FIRM" && (
                  <button
                    onClick={handleSubmitEmergency}
                    className="mypets-btn-emergency"
                  >
                    <span className="mypets-btn-emergency-icon" aria-hidden="true" />
                    <span className="mypets-btn-text">{t("petDashboard.submitEmergency")}</span>
                  </button>
                )}
              </div>
            </div>

            {isEmpty ? (
              <div className={`mypets-empty ${isRTL ? 'rtl' : 'ltr'}`}>
                <div className="mypets-empty-emoji">🐾</div>
                <h3 className="mypets-empty-title">
                  {t("petDashboard.noPetsTitle")}
                </h3>
                <p className="mypets-empty-description">
                  {t("petDashboard.noPetsDescription")}
                </p>
                <button
                  onClick={handleAddNewPet}
                  className="mypets-btn-primary"
                  style={{ background: colors.primary }}
                >
                  {t("petDashboard.addFirstPet")}
                </button>


              </div>
            ) : (
              <div className="pets-grid mypets-grid">
                {pets.map((pet) => {
                  const imageSrc = resolvePetImageSrc(pet);
                  const isPlaceholder = imageSrc === "/images/no-image-pet.png";

                  // Generate health status class based on health value
                  const getHealthStatusClass = (health) => {
                    if (!health) return 'health-status';
                    const normalized = health.toLowerCase().replace(/\s+/g, '-');
                    return `health-status ${normalized}`;
                  };

                  // Meta line: skip breed when it just repeats the species
                  const metaParts = [pet.species, pet.breed, pet.age]
                    .filter(Boolean)
                    .filter((part, idx, arr) => idx === 0 || part !== arr[0]);

                  return (
                    <div
                      key={pet.id}
                      className="mypets-card"
                      style={{
                        '--user-type-color': userTypeColor
                      }}
                    >
                      <div className={`mypets-card-image ${isPlaceholder ? 'is-placeholder' : ''}`}>
                        <img
                          src={imageSrc}
                          alt={`${pet.name}'s Photo`}
                          className="mypets-card-img"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/images/no-image-pet.png";
                            e.target.closest('.mypets-card-image')?.classList.add('is-placeholder');
                          }}
                        />
                        <div className="mypets-card-image-paw" aria-hidden="true" />
                      </div>

                      <div className={`mypets-card-content ${isRTL ? 'rtl' : 'ltr'}`}>
                        <h3 className="mypets-card-name">
                          {pet.name}
                        </h3>

                        <p className="mypets-card-meta">
                          {metaParts.join(' · ') || t("petDashboard.notSpecified")}
                        </p>

                        {(pet.health || pet.adopted_rescued) && (
                          <div className="mypets-card-pills">
                            {pet.health && (
                              <span className={getHealthStatusClass(pet.health)}>
                                {pet.health}
                              </span>
                            )}
                            {pet.adopted_rescued && (
                              <span className={`adopted-rescued-badge ${pet.adopted_rescued.toLowerCase()}`}>
                                {pet.adopted_rescued.toLowerCase() === "adopted"
                                  ? t("addPet.adopted")
                                  : pet.adopted_rescued.toLowerCase() === "rescued"
                                    ? t("addPet.rescued")
                                    : pet.adopted_rescued}
                              </span>
                            )}
                          </div>
                        )}

                        <div className="mypets-card-actions">
                          {/* Show edit button for all guardians/owners since they are already filtered in the hook */}
                          <button
                            onClick={() => handlePetAction("view", pet.id)}
                            className="mypets-action-btn mypets-action-view"
                          >
                            {t("petDashboard.viewProfile")}
                          </button>
                          <button
                            onClick={() => handlePetAction("update", pet.id)}
                            className="mypets-action-btn mypets-action-edit"
                          >
                            {t("petDashboard.editProfile")}
                          </button>
                          {/* Only show delete button if user is the primary owner */}
                          {user?.id && pet.ownerId && String(user.id) === String(pet.ownerId) && (
                            <button
                              onClick={() => handlePetAction("delete", pet.id)}
                              disabled={deletingPet && petToDelete === pet.id}
                              className={`mypets-action-btn mypets-action-delete ${deletingPet && petToDelete === pet.id ? 'disabled' : ''}`}
                            >
                              {deletingPet && petToDelete === pet.id
                                ? "..."
                                : t("petDashboard.deleteProfile")}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isLoading={deletingPet}
        title="Delete Pet"
        message="Are you sure you want to delete this pet? This action cannot be undone."
      />

    </div>
  );
}
